<div class="modal fade" id="chemicalImportModal" tabindex="-1">
  <div class="modal-dialog modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-upload me-2"></i>
          Import Chemicals from CSV
        </h5>
        <button type="button" class="btn-close" aria-label="Close" (click)="closeModal()"></button>
      </div>
      <div class="modal-body">
        <!-- Error Message -->
        <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible" role="alert">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          {{ errorMessage }}
          <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
        </div>

        <!-- Instructions -->
        <div class="mb-4">
          <p class="text-muted mb-2">
            Upload a CSV file with chemical data. Required columns: <strong>name</strong> and <strong>category</strong>.
            Optional columns include signalWord, epaNumber, activeIngredient, sponsor, compareTo, rupFlag, and more.
            Column headers are detected automatically regardless of order.
          </p>
          <div class="alert alert-info py-2 mb-2" role="alert">
            <i class="bi bi-lightbulb me-1"></i>
            <strong>Tip:</strong> Include the <strong>epaNumber</strong> column for best EPA data enrichment results.
            The <strong>sponsor</strong> column will automatically look up or create sponsor records.
          </div>
          <button type="button" class="btn btn-outline-primary btn-sm" (click)="downloadTemplate()">
            <i class="bi bi-download me-1"></i>
            Download Template
          </button>
        </div>

        <!-- Import Options -->
        <div class="mb-4">
          <div class="form-check">
            <input
              type="checkbox"
              class="form-check-input"
              id="enrichFromEpa"
              [(ngModel)]="enrichFromEpa"
            />
            <label class="form-check-label" for="enrichFromEpa">
              <strong>Auto-populate from EPA database</strong>
              <small class="d-block text-muted">
                After import, look up EPA registration numbers, signal words, and other data from our EPA database
              </small>
            </label>
          </div>
        </div>

        <!-- File Selection -->
        <div class="mb-4">
          <label class="form-label">Select CSV File</label>
          <input
            type="file"
            class="form-control"
            accept=".csv"
            (change)="onFileSelected($event)"
          />
          <small class="form-text text-muted" *ngIf="selectedFile">
            Selected: {{ selectedFile.name }} ({{ (selectedFile.size / 1024).toFixed(1) }} KB)
          </small>
        </div>

        <!-- Validation Preview -->
        <div *ngIf="preview" class="preview-section">
          <h6 class="mb-3">Validation Preview</h6>

          <!-- Summary Stats -->
          <div class="d-flex gap-3 mb-3">
            <span class="badge bg-secondary">{{ preview.totalRows }} total rows</span>
            <span class="badge bg-success">{{ preview.validRows }} valid</span>
            <span class="badge bg-danger" *ngIf="preview.invalidRows > 0">{{ preview.invalidRows }} invalid</span>
          </div>

          <!-- Preview Table -->
          <div class="table-responsive preview-table-container overflow-y-auto">
            <table class="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th style="width: 50px;">Row</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Signal Word <small class="text-muted">(optional)</small></th>
                  <th style="width: 80px;">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of preview.rows" [class.bg-danger-subtle]="!row.isValid">
                  <td>{{ row.rowNumber }}</td>
                  <td>
                    <div class="text-truncate" style="max-width: 200px;">{{ row.name || '-' }}</div>
                  </td>
                  <td>{{ row.category || '-' }}</td>
                  <td>{{ row.signalWord || '-' }}</td>
                  <td>
                    <i *ngIf="row.isValid" class="bi bi-check-circle text-success"></i>
                    <i *ngIf="!row.isValid" class="bi bi-x-circle text-danger"></i>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Validation Errors -->
          <div *ngIf="preview.invalidRows > 0" class="mt-3">
            <h6 class="text-danger">
              <i class="bi bi-exclamation-triangle me-1"></i>
              Validation Errors
            </h6>
            <div class="errors-list overflow-y-auto p-2 bg-body-tertiary rounded-1">
              <div *ngFor="let row of preview.rows">
                <div *ngIf="!row.isValid" class="py-2">
                  <strong>Row {{ row.rowNumber }}:</strong>
                  <ul class="mb-0 mt-1 ps-4 small text-body-secondary">
                    <li *ngFor="let error of row.errors">{{ error }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Import Progress -->
        <div *ngIf="importing" class="mt-4 p-3 bg-body-tertiary rounded border">
          <h6 class="mb-3">
            <i class="bi bi-arrow-repeat me-2" [class.spin]="importStatus !== 'complete' && importStatus !== 'failed'"></i>
            Import Progress
          </h6>

          <!-- Progress Bar -->
          <div class="progress mb-2" style="height: 24px;">
            <div
              class="progress-bar"
              [class.bg-success]="importStatus === 'complete'"
              [class.bg-danger]="importStatus === 'failed'"
              [class.progress-bar-striped]="importStatus !== 'complete' && importStatus !== 'failed'"
              [class.progress-bar-animated]="importStatus !== 'complete' && importStatus !== 'failed'"
              role="progressbar"
              [style.width.%]="importProgress"
            >
              {{ importProgress }}%
            </div>
          </div>

          <!-- Status Message -->
          <div class="text-center">
            <span [class.text-success]="importStatus === 'complete'" [class.text-danger]="importStatus === 'failed'">
              <i *ngIf="importStatus === 'complete'" class="bi bi-check-circle me-1"></i>
              <i *ngIf="importStatus === 'failed'" class="bi bi-x-circle me-1"></i>
              {{ statusMessage }}
            </span>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" (click)="closeModal()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="importChemicals()"
          [disabled]="!canImport"
        >
          <span *ngIf="importing" class="spinner-border spinner-border-sm me-1"></span>
          Import {{ preview?.validRows || 0 }} Chemical{{ preview?.validRows === 1 ? '' : 's' }}
        </button>
      </div>
    </div>
  </div>
</div>
